import {
  configureStore,
  combineReducers,
} from "@reduxjs/toolkit";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { persistReducer, persistStore, createTransform } from "redux-persist";
import storage from "redux-persist/lib/storage";

import userReducer from "./userReducer";
import modalReducer from "./modalSlice";
import travelSearchReducer from "./travelSearchSlice";
import {
  INITIAL_FLIGHT_FORM,
  INITIAL_HOTEL_FORM,
} from "./travelSearchSlice";
import agentReducer from "./agentSlice";
import { apiSlice } from "./apiSlice";
import { getLocalISODate } from "@utils/dateUtils";

const persistConfig = {
  key: "root",
  storage,
  stateReconciler: autoMergeLevel2,
};

// Separate persist config for user (modal should NOT persist)
const userPersistConfig = {
  ...persistConfig,
  key: "user",
};

const today = () => getLocalISODate();

function sanitizeFlightForm(formData) {
  if (!formData || typeof formData !== "object") return formData;
  const now = today();
  const out = { ...formData };
  if (out.departureDate && out.departureDate < now) out.departureDate = "";
  if (out.returnDate && out.returnDate < now) out.returnDate = "";
  return out;
}

function sanitizeHotelForm(formData) {
  if (!formData || typeof formData !== "object") return formData;
  const now = today();
  const out = { ...formData };
  if (out.checkIn && out.checkIn < now) out.checkIn = "";
  if (out.checkOut && out.checkOut < now) out.checkOut = "";
  return out;
}

function omitTabSearchId(tab) {
  if (!tab || typeof tab !== "object" || !Object.prototype.hasOwnProperty.call(tab, "searchId")) {
    return tab;
  }
  const rest = { ...tab };
  delete rest.searchId;
  return rest;
}

function stripUnusedTabSearchIds(tabs) {
  if (!tabs || typeof tabs !== "object") return tabs;
  return {
    ...tabs,
    flights: omitTabSearchId(tabs.flights),
    hotels: omitTabSearchId(tabs.hotels),
  };
}

function sanitizeTabForms(tabs) {
  if (!tabs || typeof tabs !== "object") return tabs;
  return {
    ...tabs,
    flights: tabs.flights?.formData
      ? { ...tabs.flights, formData: sanitizeFlightForm(tabs.flights.formData) }
      : tabs.flights,
    hotels: tabs.hotels?.formData
      ? { ...tabs.hotels, formData: sanitizeHotelForm(tabs.hotels.formData) }
      : tabs.hotels,
  };
}

/** Migrate pre–per-tab-form persisted shape (single root formData). */
function migrateLegacyTravelSearch(state) {
  if (!state || typeof state !== "object") return state;
  const hasPerTabForms =
    state.tabs?.flights?.formData && state.tabs?.hotels?.formData;
  if (hasPerTabForms) {
    const { formData: _legacy, ...rest } = state;
    return rest;
  }

  const legacy = state.formData || {};
  const nextTabs = state.tabs || {};

  const pickFlight = {
    ...INITIAL_FLIGHT_FORM,
    origin: legacy.origin ?? "",
    destination: legacy.destination ?? "",
    departureDate: legacy.departureDate ?? "",
    returnDate: legacy.returnDate ?? "",
    passengers: legacy.passengers ?? 1,
    currency: legacy.currency ?? "GEL",
  };
  const pickHotel = {
    ...INITIAL_HOTEL_FORM,
    city: legacy.city ?? "",
    checkIn: legacy.checkIn ?? "",
    checkOut: legacy.checkOut ?? "",
    guests: legacy.guests ?? 1,
  };
  return {
    ...state,
    formData: undefined,
    tabs: {
      flights: {
        results: [],
        hasSearched: false,
        searchTimestamp: null,
        currencyFallback: false,
        scraperSearchId: null,
        flightBuySearchId: null,
        providerStatuses: {},
        ...nextTabs.flights,
        formData: pickFlight,
      },
      hotels: {
        results: [],
        hasSearched: false,
        searchTimestamp: null,
        currencyFallback: false,
        scraperSearchId: null,
        flightBuySearchId: null,
        providerStatuses: {},
        ...nextTabs.hotels,
        formData: pickHotel,
      },
    },
  };
}

const travelSearchSanitizeTransform = createTransform(
  null,
  (outboundState) => {
    if (!outboundState || typeof outboundState !== "object") return outboundState;
    let next = migrateLegacyTravelSearch(outboundState);
    next = {
      ...next,
      tabs: stripUnusedTabSearchIds(sanitizeTabForms(next.tabs)),
    };
    if (next.searchType === "all" || next.searchType === "tours") {
      next = { ...next, searchType: "flights" };
    }
    if (next.tabs && (next.tabs.all || next.tabs.tours)) {
      const restTabs = { ...next.tabs };
      delete restTabs.all;
      delete restTabs.tours;
      next = { ...next, tabs: restTabs };
    }
    return next;
  },
  { whitelist: ["travelSearch"] }
);

const travelSearchPersistConfig = {
  ...persistConfig,
  key: "travelSearch",
  transforms: [travelSearchSanitizeTransform],
};

const rootReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  modal: modalReducer, // Modal state should NOT persist
  travelSearch: persistReducer(travelSearchPersistConfig, travelSearchReducer),
  agent: agentReducer,
  [apiSlice.reducerPath]: apiSlice.reducer, // RTK Query API slice
});

const clearSessionCache = ({ dispatch }) => next => action => {
  const result = next(action);
  if (["user/logOut", "user/expireSession"].includes(action.type)) {
    dispatch(apiSlice.util.resetApiState());
  }
  return result;
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Allow redux-persist actions
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }).concat(clearSessionCache, apiSlice.middleware), // Add RTK Query middleware
});

export default store;

export const persistor = persistStore(store);
